import React, { useState, useRef } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import toast from "react-hot-toast";
import {
    useAdminParticipants,
    useAdminParticipantStats,
    useParticipantInstitutions,
    useCreateParticipant,
    useUpdateParticipant,
    useDeleteParticipant,
} from "@/services/adminParticipantService";
import type { Participant, ParticipantFilters } from "@/services/adminParticipantService";
import { useReligions, useCountries } from "@/services/lookupService";
import { usePeriods } from "@/services/usePeriods";
import { ITEMS_BLOOD_TYPE, ITEMS_CLOTHES_SIZE } from "@/constants/options";
import { axiosClient } from "@/lib/axiosClient";
import { useUser } from "@/context/useUser";
import ExportButton from "@/components/private/pic/ExportButton";

import { Button } from "@/components/ui/button";
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Edit2, Plus, Trash2, Users2, Loader2, Upload, Search, ChevronDown, ChevronRight } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";

// Schema Validation
const formSchema = z.object({
    full_name: z.string().min(1, "Nama lengkap wajib diisi"),
    gender: z.string().optional().nullable(),
    email: z.string().email("Email tidak valid").optional().or(z.literal("")),
    nik: z.string().optional().nullable(),
    phone_mobile: z.string().optional().nullable(),
    phone_number: z.string().optional().nullable(),
    institution: z.string().optional().nullable(),
    institution_address: z.string().optional().nullable(),
    position: z.string().optional().nullable(),
    address: z.string().optional().nullable(),
    city: z.string().optional().nullable(),
    postal_code: z.string().optional().nullable(),
    blood_type: z.string().optional().nullable(),
    clothing_size: z.string().optional().nullable(),
    birth_date: z.string().optional().nullable(),
    birth_place: z.string().optional().nullable(),
    religion_id: z.number().optional().nullable(),
    country_id: z.number().optional().nullable(),
    registration_type: z.string().optional().nullable(),
    is_active: z.boolean().default(true),
    is_valid: z.boolean().default(true),
    sync_user: z.boolean().optional(),
});
type FormValues = z.infer<typeof formSchema>;

const emptyFilters: ParticipantFilters = {
    institution: "",
    gender: "",
    blood_type: "",
    clothing_size: "",
    registration_type: "",
    is_active: "",
    is_valid: "",
    religion_id: "",
    country_id: "",
    period_id: "",
};

interface ParticipantsPageProps {
    defaultFilters?: Partial<ParticipantFilters>;
    hideHeader?: boolean;
}

export default function ParticipantsPage({ defaultFilters = {}, hideHeader = false }: ParticipantsPageProps = {}) {
    const { data: currentUser } = useUser();
    // Petugas RFID (role 7) hanya boleh mendaftarkan RFID — tidak boleh export/tambah/edit/hapus data peserta.
    const isPetugasRfid = currentUser?.role_id === 7;
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const [search, setSearch] = useState("");
    const [filters, setFilters] = useState<ParticipantFilters>({ ...emptyFilters, ...defaultFilters });

    const updateFilter = (key: keyof ParticipantFilters, value: string) => {
        setFilters((prev) => ({ ...prev, [key]: value }));
        setPage(1);
    };

    const resetFilters = () => {
        setFilters({ ...emptyFilters, ...defaultFilters });
        setSearch("");
        setPage(1);
    };

    const isFiltering = Boolean(search) || Object.values(filters).some((v) => v);

    // Asumsi return data dari hook ini berbentuk paginated data dari Laravel
    const { data: responseData, isLoading } = useAdminParticipants(page, search, perPage, filters);
    const { data: stats } = useAdminParticipantStats(search, filters);
    const { data: institutionOptions = [] } = useParticipantInstitutions();
    const participants: Participant[] = responseData?.data || [];
    const totalPages = responseData?.last_page || 1;
    const totalItems = stats?.total ?? responseData?.total ?? 0;
    const fromItem = responseData?.from || 0;
    const toItem = responseData?.to || 0;

    const createMutation = useCreateParticipant();
    const updateMutation = useUpdateParticipant();
    const deleteMutation = useDeleteParticipant();

    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [editingParticipant, setEditingParticipant] = useState<Participant | null>(null);

    // Alert dialog state
    const [isAlertOpen, setIsAlertOpen] = useState(false);
    const [alertConfig, setAlertConfig] = useState<{
        title: string;
        description: string;
        actionText: string;
        action: () => void;
        isDestructive?: boolean;
    } | null>(null);

    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [isLoadingEdit, setIsLoadingEdit] = useState(false);

    const { data: religions = [] } = useReligions();
    const { data: countries = [] } = useCountries();
    const { data: periods = [] } = usePeriods();

    // --- Row expand/collapse (detail view) ---
    const [expandedId, setExpandedId] = useState<number | null>(null);
    const [expandedDetails, setExpandedDetails] = useState<Record<number, Participant>>({});
    const [loadingDetailId, setLoadingDetailId] = useState<number | null>(null);

    const toggleExpand = async (participant: Participant) => {
        const id = participant.participant_id;
        if (expandedId === id) {
            setExpandedId(null);
            return;
        }
        setExpandedId(id);
        if (expandedDetails[id]) return;

        setLoadingDetailId(id);
        try {
            const { data } = await axiosClient.get(`/participants/${id}`, { withCredentials: true });
            setExpandedDetails((prev) => ({ ...prev, [id]: data.data }));
        } catch {
            // fall back to the row data already available in the list
        } finally {
            setLoadingDetailId(null);
        }
    };

    const religionName = (id?: number | null) => religions.find((r) => r.id === id)?.name || "-";
    const countryName = (id?: number | null) => countries.find((c) => c.id === id)?.name || "-";

    const formatDate = (value?: string | null) => {
        if (!value) return "-";
        const d = new Date(value);
        if (isNaN(d.getTime())) return value;
        return d.toLocaleDateString("id-ID", { day: "2-digit", month: "long", year: "numeric" });
    };

    const isMutating = createMutation.isPending || updateMutation.isPending;

    const emptyDefaults: FormValues = {
        full_name: "",
        gender: "",
        email: "",
        nik: "",
        phone_mobile: "",
        phone_number: "",
        institution: "",
        institution_address: "",
        position: "",
        address: "",
        city: "",
        postal_code: "",
        blood_type: "",
        clothing_size: "",
        birth_date: "",
        birth_place: "",
        religion_id: null,
        country_id: null,
        registration_type: "personal",
        is_active: true,
        is_valid: true,
        sync_user: false,
    };

    const form = useForm<FormValues>({
        resolver: zodResolver(formSchema),
        defaultValues: emptyDefaults,
    });

    const participantToFormValues = (participant: Participant): FormValues => ({
        full_name: participant.full_name,
        gender: participant.gender || "",
        email: participant.email || "",
        nik: participant.nik || "",
        phone_mobile: participant.phone_mobile || "",
        phone_number: participant.phone_number || "",
        institution: participant.institution || "",
        institution_address: participant.institution_address || "",
        position: participant.position || "",
        address: participant.address || "",
        city: participant.city || "",
        postal_code: participant.postal_code || "",
        blood_type: participant.blood_type || "",
        clothing_size: participant.clothing_size || "",
        birth_date: participant.birth_date ? participant.birth_date.slice(0, 10) : "",
        birth_place: participant.birth_place || "",
        religion_id: participant.religion_id ?? null,
        country_id: participant.country_id ?? null,
        registration_type: participant.registration_type || "personal",
        is_active: Boolean(participant.is_active),
        is_valid: Boolean(participant.is_valid),
        sync_user: Boolean(participant.linked_user),
    });

    const handleOpenDialog = async (participant?: Participant) => {
        setSelectedFile(null);

        if (!participant) {
            setEditingParticipant(null);
            form.reset(emptyDefaults);
            setIsDialogOpen(true);
            return;
        }

        setEditingParticipant(participant);
        form.reset(participantToFormValues(participant));
        setIsDialogOpen(true);

        // Fetch full record (includes linked_user info for the account-sync option)
        setIsLoadingEdit(true);
        try {
            const { data } = await axiosClient.get(`/participants/${participant.participant_id}`, { withCredentials: true });
            const fullParticipant: Participant = data.data;
            setEditingParticipant(fullParticipant);
            form.reset(participantToFormValues(fullParticipant));
        } catch {
            // Keep using the row data already shown if the detail fetch fails
        } finally {
            setIsLoadingEdit(false);
        }
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            setSelectedFile(e.target.files[0]);
        }
    };

    const onSubmit = (values: FormValues) => {
        const formData = new FormData();
        const isEditingWithLinkedUser = Boolean(editingParticipant?.linked_user);

        // Append all text fields
        Object.entries(values).forEach(([key, value]) => {
            if (key === "sync_user" && !isEditingWithLinkedUser) {
                return; // irrelevant when there's no linked account
            }
            if (value !== null && value !== undefined) {
                if (typeof value === 'boolean') {
                    formData.append(key, value ? '1' : '0');
                } else {
                    formData.append(key, String(value));
                }
            }
        });

        if (selectedFile) {
            formData.append("photo", selectedFile);
        }

        if (editingParticipant) {
            formData.append("_method", "PUT"); // Important for Laravel multipart/form-data updates
            updateMutation.mutate(
                { id: editingParticipant.participant_id, payload: formData },
                {
                    onSuccess: () => {
                        toast.success("Partisipan berhasil diperbarui");
                        setIsDialogOpen(false);
                    },
                    onError: (error: any) => {
                        toast.error(error?.response?.data?.message || "Gagal memperbarui partisipan");
                    },
                }
            );
        } else {
            createMutation.mutate(formData, {
                onSuccess: () => {
                    toast.success("Partisipan berhasil ditambahkan");
                    setIsDialogOpen(false);
                },
                onError: (error: any) => {
                    toast.error(error?.response?.data?.message || "Gagal menambahkan partisipan");
                },
            });
        }
    };

    const handleDelete = (id: number) => {
        setAlertConfig({
            title: "Hapus Partisipan",
            description: "Apakah Anda yakin ingin menghapus partisipan ini? Tindakan ini tidak dapat dibatalkan.",
            actionText: "Hapus",
            isDestructive: true,
            action: () => {
                deleteMutation.mutate(id, {
                    onSuccess: () => toast.success("Partisipan berhasil dihapus"),
                    onError: (error: any) => toast.error(error?.response?.data?.message || "Gagal menghapus partisipan"),
                });
            }
        });
        setIsAlertOpen(true);
    };

    const linkedUser = editingParticipant?.linked_user || null;

    const exportParams = new URLSearchParams();
    if (search) exportParams.set("search", search);
    Object.entries(filters).forEach(([key, value]) => {
        if (value) exportParams.set(key, String(value));
    });
    const exportQuery = exportParams.toString();
    const exportEndpoint = `/participants/export${exportQuery ? `?${exportQuery}` : ""}`;

    return (
        <div className="max-w-7xl mx-auto space-y-8 pb-20 px-4 md:px-0">
            {/* --- HEADER --- */}
            {!hideHeader && (
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 bg-white dark:bg-slate-900 p-6 md:p-8 rounded-3xl shadow-sm border border-slate-100 dark:border-slate-800">
                <div>
                    <div className="flex items-center gap-3 mb-2">
                        <div className="p-2.5 rounded-xl bg-indigo-50 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400">
                            <Users2 className="w-6 h-6" />
                        </div>
                        <h1 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight">Data Partisipan</h1>
                    </div>
                    <p className="text-sm text-slate-500 dark:text-slate-400 max-w-xl">
                        Manajemen master data partisipan yang terdaftar di sistem.
                    </p>
                </div>
                {!isPetugasRfid && (
                    <div className="flex items-center gap-3 shrink-0 flex-wrap">
                        <ExportButton
                            endpoint={exportEndpoint}
                            filenamePrefix="partisipan"
                            className="h-11 px-6 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white border-emerald-600 hover:border-emerald-700 dark:hover:bg-emerald-700 dark:hover:text-white font-bold shadow-md min-w-0"
                        />
                        <Button onClick={() => handleOpenDialog()} className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl h-11 px-6 font-bold shadow-md transition-all">
                            <Plus className="w-4 h-4 mr-2" /> Tambah Partisipan
                        </Button>
                    </div>
                )}
            </div>
            )}

            {/* --- SUMMARY CARDS --- */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                <div className="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-sm flex flex-col justify-between h-[100px] relative overflow-hidden group">
                    <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                        <Users2 className="w-12 h-12 text-indigo-500" />
                    </div>
                    <p className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest flex items-center gap-1.5"><Users2 className="w-4 h-4 text-indigo-500" /> {isFiltering ? "Hasil Filter" : "Total Partisipan"}</p>
                    <p className="text-3xl font-black text-slate-900 dark:text-white">{totalItems}</p>
                </div>
                <div className="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-sm flex flex-col justify-between h-[100px]">
                    <p className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest">Valid</p>
                    <p className="text-3xl font-black text-green-600 dark:text-green-400">{stats?.valid ?? 0}</p>
                </div>
                <div className="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-sm flex flex-col justify-between h-[100px]">
                    <p className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest">Personal</p>
                    <p className="text-3xl font-black text-slate-900 dark:text-white">{stats?.personal ?? 0}</p>
                </div>
                <div className="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-sm flex flex-col justify-between h-[100px]">
                    <p className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest">Kolektif</p>
                    <p className="text-3xl font-black text-indigo-600 dark:text-indigo-400">{stats?.kolektif ?? 0}</p>
                </div>
                <div className="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-sm flex flex-col justify-between h-[100px]">
                    <p className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest">Laki-laki</p>
                    <p className="text-3xl font-black text-sky-600 dark:text-sky-400">{stats?.male ?? 0}</p>
                </div>
                <div className="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-sm flex flex-col justify-between h-[100px]">
                    <p className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest">Perempuan</p>
                    <p className="text-3xl font-black text-pink-600 dark:text-pink-400">{stats?.female ?? 0}</p>
                </div>
            </div>

            {/* --- TOOLBAR (FILTER & SEARCH) --- */}
            <div className="flex flex-col gap-3 w-full">
                <div className="flex flex-wrap items-center gap-3 w-full">
                    <div className="relative flex-1 min-w-[250px]">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <input
                            type="text"
                            placeholder="Cari nama, email, institusi..."
                            value={search}
                            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                            className="w-full h-11 pl-11 pr-4 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl text-sm focus:ring-2 focus:ring-indigo-500 outline-none shadow-sm transition-all font-medium"
                        />
                    </div>
                    {isFiltering && (
                        <Button variant="outline" onClick={resetFilters} className="h-11 rounded-xl">
                            Reset Filter
                        </Button>
                    )}
                </div>

                <div className="flex flex-wrap items-center gap-2">
                    <Select value={filters.period_id ? String(filters.period_id) : "__all__"} onValueChange={(v) => updateFilter("period_id", v === "__all__" ? "" : v)}>
                        <SelectTrigger className="h-10 rounded-xl w-[200px]"><SelectValue placeholder="Periode" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="__all__">Semua Periode</SelectItem>
                            {periods.map((p) => (
                                <SelectItem key={p.period_id} value={String(p.period_id)}>
                                    {p.name}{p.is_active ? " (Aktif)" : ""}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>

                    <Select value={filters.institution || "__all__"} onValueChange={(v) => updateFilter("institution", v === "__all__" ? "" : v)}>
                        <SelectTrigger className="h-10 rounded-xl w-[200px]"><SelectValue placeholder="Universitas" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="__all__">Semua Universitas</SelectItem>
                            {institutionOptions.map((inst) => (
                                <SelectItem key={inst} value={inst}>
                                    <span className="truncate block max-w-[260px]" title={inst}>{inst}</span>
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>

                    <Select value={filters.gender || "__all__"} onValueChange={(v) => updateFilter("gender", v === "__all__" ? "" : v)}>
                        <SelectTrigger className="h-10 rounded-xl w-[150px]"><SelectValue placeholder="Jenis Kelamin" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="__all__">Semua Gender</SelectItem>
                            <SelectItem value="male">Laki-laki</SelectItem>
                            <SelectItem value="female">Perempuan</SelectItem>
                        </SelectContent>
                    </Select>

                    <Select value={filters.blood_type || "__all__"} onValueChange={(v) => updateFilter("blood_type", v === "__all__" ? "" : v)}>
                        <SelectTrigger className="h-10 rounded-xl w-[150px]"><SelectValue placeholder="Golongan Darah" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="__all__">Semua Gol. Darah</SelectItem>
                            {ITEMS_BLOOD_TYPE.map((item) => (
                                <SelectItem key={item.value} value={item.value}>{item.label}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>

                    <Select value={filters.clothing_size || "__all__"} onValueChange={(v) => updateFilter("clothing_size", v === "__all__" ? "" : v)}>
                        <SelectTrigger className="h-10 rounded-xl w-[150px]"><SelectValue placeholder="Ukuran Baju" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="__all__">Semua Ukuran</SelectItem>
                            {ITEMS_CLOTHES_SIZE.map((item) => (
                                <SelectItem key={item.value} value={item.value}>{item.label}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>

                    <Select value={filters.registration_type || "__all__"} onValueChange={(v) => updateFilter("registration_type", v === "__all__" ? "" : v)}>
                        <SelectTrigger className="h-10 rounded-xl w-[160px]"><SelectValue placeholder="Tipe Registrasi" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="__all__">Semua Tipe</SelectItem>
                            <SelectItem value="personal">Personal</SelectItem>
                            <SelectItem value="kolektif">Kolektif</SelectItem>
                        </SelectContent>
                    </Select>

                    <Select value={filters.payment_status || "__all__"} onValueChange={(v) => updateFilter("payment_status", v === "__all__" ? "" : v)}>
                        <SelectTrigger className="h-10 rounded-xl w-[170px]"><SelectValue placeholder="Status Pembayaran" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="__all__">Semua Pembayaran</SelectItem>
                            <SelectItem value="paid">Lunas (Paid)</SelectItem>
                            <SelectItem value="unpaid">Belum Bayar (Unpaid)</SelectItem>
                        </SelectContent>
                    </Select>

                    <Select value={filters.is_active === "" || filters.is_active === undefined ? "__all__" : filters.is_active} onValueChange={(v) => updateFilter("is_active", v === "__all__" ? "" : v)}>
                        <SelectTrigger className="h-10 rounded-xl w-[150px]"><SelectValue placeholder="Status Aktif" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="__all__">Semua Status Aktif</SelectItem>
                            <SelectItem value="1">Aktif</SelectItem>
                            <SelectItem value="0">Tidak Aktif</SelectItem>
                        </SelectContent>
                    </Select>

                    <Select value={filters.is_valid === "" || filters.is_valid === undefined ? "__all__" : filters.is_valid} onValueChange={(v) => updateFilter("is_valid", v === "__all__" ? "" : v)}>
                        <SelectTrigger className="h-10 rounded-xl w-[150px]"><SelectValue placeholder="Status Valid" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="__all__">Semua Status Valid</SelectItem>
                            <SelectItem value="1">Valid</SelectItem>
                            <SelectItem value="0">Invalid</SelectItem>
                        </SelectContent>
                    </Select>

                    <Select value={filters.religion_id ? String(filters.religion_id) : "__all__"} onValueChange={(v) => updateFilter("religion_id", v === "__all__" ? "" : v)}>
                        <SelectTrigger className="h-10 rounded-xl w-[150px]"><SelectValue placeholder="Agama" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="__all__">Semua Agama</SelectItem>
                            {religions.map((r) => (
                                <SelectItem key={r.id} value={String(r.id)}>{r.name}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>

                    <Select value={filters.country_id ? String(filters.country_id) : "__all__"} onValueChange={(v) => updateFilter("country_id", v === "__all__" ? "" : v)}>
                        <SelectTrigger className="h-10 rounded-xl w-[170px]"><SelectValue placeholder="Kewarganegaraan" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="__all__">Semua Negara</SelectItem>
                            {countries.map((c) => (
                                <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {/* --- DATA TABLE --- */}
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden relative">
                    <div className="overflow-x-auto">
                        {isLoading ? (
                            <div className="flex items-center justify-center h-48">
                                <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
                            </div>
                        ) : (
                            <table className="w-full text-left text-sm whitespace-nowrap">
                                <thead className="bg-slate-50 dark:bg-slate-800/50 text-slate-500 dark:text-slate-400 font-bold uppercase tracking-wider text-[11px]">
                                    <tr>
                                        <th className="px-4 py-5 border-b border-slate-100 dark:border-slate-800 w-[40px]"></th>
                                        <th className="px-6 py-5 border-b border-slate-100 dark:border-slate-800 w-[50px] text-center">No</th>
                                        <th className="px-6 py-5 border-b border-slate-100 dark:border-slate-800">Nama</th>
                                        <th className="px-6 py-5 border-b border-slate-100 dark:border-slate-800">Email / No. HP</th>
                                        <th className="px-6 py-5 border-b border-slate-100 dark:border-slate-800">Institusi</th>
                                        <th className="px-6 py-5 border-b border-slate-100 dark:border-slate-800">Tipe Registrasi</th>
                                        <th className="px-6 py-5 border-b border-slate-100 dark:border-slate-800 text-center">Valid?</th>
                                        <th className="px-6 py-5 border-b border-slate-100 dark:border-slate-800 text-right sticky right-0 z-10 bg-slate-50 dark:bg-slate-800/90 shadow-[-10px_0_15px_-10px_rgba(0,0,0,0.1)]">Aksi</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                    {!participants || participants.length === 0 ? (
                                        <tr>
                                            <td colSpan={8} className="p-12 text-center text-slate-500">
                                                Belum ada data partisipan.
                                            </td>
                                        </tr>
                                    ) : (
                                        participants.map((participant, index) => {
                                            const isExpanded = expandedId === participant.participant_id;
                                            const detail = expandedDetails[participant.participant_id];
                                            const isLoadingDetail = loadingDetailId === participant.participant_id;

                                            return (
                                            <React.Fragment key={participant.participant_id}>
                                                <tr className="group hover:bg-slate-50/50 dark:hover:bg-slate-800/20 transition-colors bg-white dark:bg-slate-900">
                                                <td className="px-4 py-4 text-center">
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-7 w-7 text-slate-400 hover:text-indigo-600"
                                                        onClick={() => toggleExpand(participant)}
                                                    >
                                                        {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                                    </Button>
                                                </td>
                                                <td className="px-6 py-4 text-center font-medium">
                                                    {(page - 1) * perPage + index + 1}
                                                </td>
                                                <td className="px-6 py-4 font-semibold text-slate-900 dark:text-slate-100">
                                                    {participant.full_name}
                                                </td>
                                                <td className="px-6 py-4 text-slate-500">
                                                    <div className="font-medium text-slate-700 dark:text-slate-300">{participant.email || "-"}</div>
                                                    <div className="text-[11px] mt-1">{participant.phone_mobile || "-"}</div>
                                                </td>
                                                <td className="px-6 py-4 text-slate-600 dark:text-slate-300">
                                                    {participant.institution || "-"}
                                                </td>
                                                <td className="px-6 py-4">
                                                    <span className="capitalize font-bold text-slate-700 dark:text-slate-300 text-[11px] px-2.5 py-1 bg-slate-100 dark:bg-slate-800 rounded-md">
                                                        {participant.registration_type || "-"}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 text-center">
                                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                                        participant.is_valid
                                                            ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                                                            : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                                                    }`}>
                                                        {participant.is_valid ? 'Valid' : 'Invalid'}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 text-right sticky right-0 z-10 bg-white dark:bg-slate-900 group-hover:bg-slate-50/50 dark:group-hover:bg-slate-800/80 shadow-[-10px_0_15px_-10px_rgba(0,0,0,0.1)] transition-colors">
                                                    {isPetugasRfid ? (
                                                        <span className="text-xs text-slate-400 italic">-</span>
                                                    ) : (
                                                        <div className="flex justify-end gap-2">
                                                            <Button
                                                                variant="outline"
                                                                size="icon"
                                                                className="h-8 w-8 text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-lg"
                                                                onClick={() => handleOpenDialog(participant)}
                                                            >
                                                                <Edit2 className="h-4 w-4" />
                                                            </Button>
                                                            <Button
                                                                variant="outline"
                                                                size="icon"
                                                                className="h-8 w-8 text-rose-600 hover:text-rose-700 hover:bg-rose-50 dark:hover:bg-rose-900/20 rounded-lg"
                                                                onClick={() => handleDelete(participant.participant_id)}
                                                            >
                                                                <Trash2 className="h-4 w-4" />
                                                            </Button>
                                                        </div>
                                                    )}
                                                </td>
                                                </tr>
                                                {isExpanded && (
                                                    <tr className="bg-slate-50/70 dark:bg-slate-800/30">
                                                        <td colSpan={8} className="px-8 py-5">
                                                            {isLoadingDetail && !detail ? (
                                                                <div className="flex items-center gap-2 text-slate-500 text-sm py-4">
                                                                    <Loader2 className="w-4 h-4 animate-spin" /> Memuat detail...
                                                                </div>
                                                            ) : (
                                                                <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-4 text-sm whitespace-normal">
                                                                    <DetailItem label="NIK" value={(detail || participant).nik} />
                                                                    <DetailItem label="Jenis Kelamin" value={(detail || participant).gender === "male" ? "Laki-laki" : (detail || participant).gender === "female" ? "Perempuan" : "-"} />
                                                                    <DetailItem label="Golongan Darah" value={(detail || participant).blood_type} />
                                                                    <DetailItem label="Ukuran Baju" value={(detail || participant).clothing_size} />
                                                                    <DetailItem label="Tanggal Lahir" value={formatDate((detail || participant).birth_date)} />
                                                                    <DetailItem label="Tempat Lahir" value={(detail || participant).birth_place} />
                                                                    <DetailItem label="No. Telepon" value={(detail || participant).phone_number} />
                                                                    <DetailItem label="Jabatan" value={(detail || participant).position} />
                                                                    <DetailItem label="Agama" value={religionName((detail || participant).religion_id)} />
                                                                    <DetailItem label="Kewarganegaraan" value={countryName((detail || participant).country_id)} />
                                                                    <DetailItem label="Kota" value={(detail || participant).city} />
                                                                    <DetailItem label="Kode Pos" value={(detail || participant).postal_code} />
                                                                    <DetailItem label="Status Aktif" value={(detail || participant).is_active ? "Aktif" : "Tidak Aktif"} />
                                                                    <DetailItem
                                                                        label="Akun Login Terhubung"
                                                                        value={detail?.linked_user ? `${detail.linked_user.name} (${detail.linked_user.email})` : "Tidak ada"}
                                                                        className="col-span-2"
                                                                    />
                                                                    <DetailItem label="Alamat" value={(detail || participant).address} className="col-span-2 md:col-span-4" />
                                                                    <DetailItem label="Alamat Institusi" value={(detail || participant).institution_address} className="col-span-2 md:col-span-4" />
                                                                </div>
                                                            )}
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        )}
                    </div>
                    {/* Pagination Controls matching Conference style */}
                    {totalItems > 0 && (
                        <div className="p-4 md:p-5 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm">
                            <div className="flex items-center gap-3 text-slate-500 font-medium">
                                <span>Menampilkan {fromItem} - {toItem} dari {totalItems} data</span>
                                <div className="h-4 w-px bg-slate-300 dark:bg-slate-700 hidden sm:block"></div>
                                <div className="flex items-center gap-2 sm:flex">
                                    <label htmlFor="perPage">Tampilkan:</label>
                                    <select
                                        id="perPage"
                                        value={perPage}
                                        onChange={(e) => { setPerPage(Number(e.target.value)); setPage(1); }}
                                        className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md px-2 py-1 outline-none focus:ring-2 focus:ring-primary font-bold"
                                    >
                                        <option value="10">10</option>
                                        <option value="25">25</option>
                                        <option value="50">50</option>
                                        <option value="100">100</option>
                                    </select>
                                </div>
                            </div>
                            
                            {totalPages > 1 && (
                                <div className="flex items-center gap-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setPage(p => Math.max(1, p - 1))}
                                        disabled={page === 1}
                                        className="rounded-lg"
                                    >
                                        Sebelumnya
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                        disabled={page === totalPages}
                                        className="rounded-lg"
                                    >
                                        Selanjutnya
                                    </Button>
                                </div>
                            )}
                        </div>
                    )}
            </div>

            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            {editingParticipant ? "Edit Partisipan" : "Tambah Partisipan Baru"}
                            {isLoadingEdit && <Loader2 className="w-4 h-4 animate-spin text-indigo-500" />}
                        </DialogTitle>
                    </DialogHeader>

                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 pt-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <FormField
                                    control={form.control}
                                    name="full_name"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Nama Lengkap <span className="text-red-500">*</span></FormLabel>
                                            <FormControl>
                                                <Input placeholder="Contoh: Budi Santoso" {...field} />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                
                                <FormField
                                    control={form.control}
                                    name="email"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Email</FormLabel>
                                            <FormControl>
                                                <Input type="email" placeholder="Contoh: budi@example.com" {...field} />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={form.control}
                                    name="nik"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>NIK</FormLabel>
                                            <FormControl>
                                                <Input placeholder="Nomor Induk Kependudukan" {...field} value={field.value || ""} />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={form.control}
                                    name="phone_mobile"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Nomor HP / WhatsApp</FormLabel>
                                            <FormControl>
                                                <Input placeholder="Contoh: 08123456789" {...field} value={field.value || ""} />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={form.control}
                                    name="institution"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Institusi</FormLabel>
                                            <FormControl>
                                                <Input placeholder="Contoh: Universitas Pendidikan" {...field} value={field.value || ""} />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={form.control}
                                    name="position"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Jabatan</FormLabel>
                                            <FormControl>
                                                <Input placeholder="Contoh: Dosen" {...field} value={field.value || ""} />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={form.control}
                                    name="registration_type"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Tipe Registrasi</FormLabel>
                                            <Select onValueChange={field.onChange} value={field.value || "personal"}>
                                                <FormControl>
                                                    <SelectTrigger>
                                                        <SelectValue placeholder="Pilih Tipe" />
                                                    </SelectTrigger>
                                                </FormControl>
                                                <SelectContent>
                                                    <SelectItem value="personal">Personal</SelectItem>
                                                    <SelectItem value="kolektif">Kolektif</SelectItem>
                                                </SelectContent>
                                            </Select>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                
                                <FormField
                                    control={form.control}
                                    name="gender"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Jenis Kelamin</FormLabel>
                                            <Select onValueChange={field.onChange} value={field.value || ""}>
                                                <FormControl>
                                                    <SelectTrigger>
                                                        <SelectValue placeholder="Pilih Jenis Kelamin" />
                                                    </SelectTrigger>
                                                </FormControl>
                                                <SelectContent>
                                                    <SelectItem value="male">Laki-laki</SelectItem>
                                                    <SelectItem value="female">Perempuan</SelectItem>
                                                </SelectContent>
                                            </Select>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={form.control}
                                    name="blood_type"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Golongan Darah</FormLabel>
                                            <Select onValueChange={field.onChange} value={field.value || ""}>
                                                <FormControl>
                                                    <SelectTrigger>
                                                        <SelectValue placeholder="Pilih Golongan Darah" />
                                                    </SelectTrigger>
                                                </FormControl>
                                                <SelectContent>
                                                    {ITEMS_BLOOD_TYPE.map((item) => (
                                                        <SelectItem key={item.value} value={item.value}>{item.label}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={form.control}
                                    name="clothing_size"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Ukuran Baju</FormLabel>
                                            <Select onValueChange={field.onChange} value={field.value || ""}>
                                                <FormControl>
                                                    <SelectTrigger>
                                                        <SelectValue placeholder="Pilih Ukuran Baju" />
                                                    </SelectTrigger>
                                                </FormControl>
                                                <SelectContent>
                                                    {ITEMS_CLOTHES_SIZE.map((item) => (
                                                        <SelectItem key={item.value} value={item.value}>{item.label}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={form.control}
                                    name="birth_date"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Tanggal Lahir</FormLabel>
                                            <FormControl>
                                                <Input type="date" {...field} value={field.value || ""} />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={form.control}
                                    name="birth_place"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Tempat Lahir</FormLabel>
                                            <FormControl>
                                                <Input placeholder="Contoh: Denpasar" {...field} value={field.value || ""} />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={form.control}
                                    name="phone_number"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Nomor Telepon (Rumah/Kantor)</FormLabel>
                                            <FormControl>
                                                <Input placeholder="Contoh: 0361123456" {...field} value={field.value || ""} />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={form.control}
                                    name="religion_id"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Agama</FormLabel>
                                            <Select
                                                onValueChange={(v) => field.onChange(v ? Number(v) : null)}
                                                value={field.value ? String(field.value) : ""}
                                            >
                                                <FormControl>
                                                    <SelectTrigger>
                                                        <SelectValue placeholder="Pilih Agama" />
                                                    </SelectTrigger>
                                                </FormControl>
                                                <SelectContent>
                                                    {religions.map((r) => (
                                                        <SelectItem key={r.id} value={String(r.id)}>{r.name}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={form.control}
                                    name="country_id"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Kewarganegaraan</FormLabel>
                                            <Select
                                                onValueChange={(v) => field.onChange(v ? Number(v) : null)}
                                                value={field.value ? String(field.value) : ""}
                                            >
                                                <FormControl>
                                                    <SelectTrigger>
                                                        <SelectValue placeholder="Pilih Negara" />
                                                    </SelectTrigger>
                                                </FormControl>
                                                <SelectContent>
                                                    {countries.map((c) => (
                                                        <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={form.control}
                                    name="city"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Kota</FormLabel>
                                            <FormControl>
                                                <Input placeholder="Contoh: Denpasar" {...field} value={field.value || ""} />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={form.control}
                                    name="postal_code"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Kode Pos</FormLabel>
                                            <FormControl>
                                                <Input placeholder="Contoh: 80233" {...field} value={field.value || ""} />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>

                            <FormField
                                control={form.control}
                                name="address"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Alamat (Opsional)</FormLabel>
                                        <FormControl>
                                            <Textarea 
                                                placeholder="Alamat lengkap" 
                                                className="resize-none"
                                                {...field} 
                                                value={field.value || ""}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="institution_address"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Alamat Institusi (Opsional)</FormLabel>
                                        <FormControl>
                                            <Textarea
                                                placeholder="Alamat lengkap institusi"
                                                className="resize-none"
                                                {...field}
                                                value={field.value || ""}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            {linkedUser && (
                                <FormField
                                    control={form.control}
                                    name="sync_user"
                                    render={({ field }) => (
                                        <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4 mt-2 bg-indigo-50/50 dark:bg-indigo-900/10 border-indigo-100 dark:border-indigo-900/40">
                                            <div className="space-y-0.5 pr-4">
                                                <FormLabel className="text-base">Perbarui juga akun login</FormLabel>
                                                <p className="text-xs text-slate-500 dark:text-slate-400">
                                                    Peserta ini terhubung ke akun login "{linkedUser.name}" ({linkedUser.email}).
                                                    Aktifkan untuk ikut memperbarui nama & email akun tersebut.
                                                </p>
                                            </div>
                                            <FormControl>
                                                <Switch
                                                    checked={field.value}
                                                    onCheckedChange={field.onChange}
                                                />
                                            </FormControl>
                                        </FormItem>
                                    )}
                                />
                            )}

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <FormField
                                    control={form.control}
                                    name="is_active"
                                    render={({ field }) => (
                                        <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4 mt-2">
                                            <div className="space-y-0.5">
                                                <FormLabel className="text-base">Status Aktif</FormLabel>
                                            </div>
                                            <FormControl>
                                                <Switch
                                                    checked={field.value}
                                                    onCheckedChange={field.onChange}
                                                />
                                            </FormControl>
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={form.control}
                                    name="is_valid"
                                    render={({ field }) => (
                                        <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4 mt-2">
                                            <div className="space-y-0.5">
                                                <FormLabel className="text-base">Valid?</FormLabel>
                                            </div>
                                            <FormControl>
                                                <Switch
                                                    checked={field.value}
                                                    onCheckedChange={field.onChange}
                                                />
                                            </FormControl>
                                        </FormItem>
                                    )}
                                />
                            </div>

                            <FormItem className="mt-4">
                                <FormLabel>Foto Partisipan (Opsional)</FormLabel>
                                <div className="mt-2 flex items-center gap-4">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => fileInputRef.current?.click()}
                                    >
                                        <Upload className="h-4 w-4 mr-2" />
                                        Pilih Foto
                                    </Button>
                                    <span className="text-sm text-muted-foreground">
                                        {selectedFile ? selectedFile.name : "Belum ada file dipilih"}
                                    </span>
                                </div>
                                <input 
                                    type="file" 
                                    className="hidden" 
                                    ref={fileInputRef} 
                                    accept="image/*"
                                    onChange={handleFileChange}
                                />
                                <FormMessage />
                            </FormItem>

                            <div className="flex gap-3 pt-4 justify-end">
                                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="rounded-xl h-11 px-6 border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800">
                                    Batal
                                </Button>
                                <Button type="submit" disabled={isMutating} className="rounded-xl h-11 px-6 bg-indigo-600 hover:bg-indigo-700 text-white font-bold shadow-md">
                                    {isMutating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                    Simpan
                                </Button>
                            </div>
                        </form>
                    </Form>
                </DialogContent>
            </Dialog>

            {/* Custom Alert Dialog for Confirmations */}
            <AlertDialog open={isAlertOpen} onOpenChange={setIsAlertOpen}>
                <AlertDialogContent className="rounded-3xl border-slate-200 dark:border-slate-800 p-6 shadow-2xl max-w-sm">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="text-xl font-bold text-slate-900 dark:text-white">
                            {alertConfig?.title}
                        </AlertDialogTitle>
                        <AlertDialogDescription className="text-slate-500 dark:text-slate-400">
                            {alertConfig?.description}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter className="mt-6 gap-2">
                        <AlertDialogCancel className="rounded-xl h-11 border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 font-medium">Batal</AlertDialogCancel>
                        <AlertDialogAction 
                            onClick={() => {
                                alertConfig?.action();
                                setIsAlertOpen(false);
                            }}
                            className={`rounded-xl h-11 font-bold text-white shadow-md ${
                                alertConfig?.isDestructive 
                                    ? "bg-rose-600 hover:bg-rose-700" 
                                    : "bg-indigo-600 hover:bg-indigo-700"
                            }`}
                        >
                            {alertConfig?.actionText}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

function DetailItem({ label, value, className = "" }: { label: string; value?: string | null; className?: string }) {
    return (
        <div className={className}>
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">{label}</p>
            <p className="text-slate-700 dark:text-slate-300 mt-0.5">{value || "-"}</p>
        </div>
    );
}
